import { useRef } from "react";
import { animate, motion, useMotionValue, useMotionValueEvent, useScroll, useTransform } from "framer-motion";
import { aiChips, aiSteps } from "../../data/phase2Content";
import { people } from "../../data/demoContent";
import { useElementWidth } from "../../hooks/useMedia";
import { AIChip } from "./AIChip";
import { AIHubContent, AIHubPending } from "./AIHub";
import { AIRecommendationCard } from "./AIRecommendationCard";
import { AIControls } from "./AIControls";

const S = 520;
const C = 260;
const POS = [-90, -30, 30, 90, 150, 210].map((a) => {
  const r = (a * Math.PI) / 180;
  return { x: C + 205 * Math.cos(r), y: C + 200 * Math.sin(r) };
});
const win = (i) => [0.36 + i * 0.04, 0.44 + i * 0.04];

const ChipNode = ({ chip, i, p, consent }) => {
  const [a, b] = win(i);
  const k = useTransform(p, [a, b], [0, 1]);
  const pos = POS[i];
  const x = useTransform(k, [0, 1], [(pos.x - C) * 0.4, 0]);
  const y = useTransform(k, [0, 1], [(pos.y - C) * 0.4, 0]);
  const opacity = useTransform(p, [0, 0.3, a, b], [0, 0.45, 0.6, 1]);
  return (
    <div className="absolute z-10" style={{ left: pos.x, top: pos.y, transform: "translate(-50%, -50%)" }}>
      <motion.div style={{ x, y, opacity }}>
        <AIChip chip={chip} consent={consent} />
      </motion.div>
    </div>
  );
};

const Line = ({ i, p, consent }) => {
  const len = useTransform(p, win(i), [0, 1]);
  const off = aiChips[i].gate && !consent;
  return <motion.line x1={POS[i].x} y1={POS[i].y} x2={C} y2={C} stroke={off ? "#777B8D" : aiChips[i].color} strokeWidth={1} strokeOpacity={off ? 0.3 : 0.7} style={{ pathLength: len }} />;
};

const Hub = ({ p, consent }) => {
  const formed = useTransform(p, [0.62, 0.72], [0, 1]);
  const pending = useTransform(formed, [0, 1], [1, 0]);
  const scale = useTransform(formed, [0, 1], [0.92, 1]);
  return (
    <motion.div style={{ scale, left: C - 115, top: C - 80 }} className="glass glass-heavy absolute z-20 h-[160px] w-[230px] rounded-[30px] p-4" data-testid="ai-hub">
      <motion.div style={{ opacity: pending }} className="absolute inset-0"><AIHubPending /></motion.div>
      <motion.div style={{ opacity: formed }} className="absolute inset-0 p-4"><AIHubContent consent={consent} /></motion.div>
    </motion.div>
  );
};

const Stage = ({ p, consent }) => {
  const ref = useRef(null);
  const w = useElementWidth(ref, S);
  const s = Math.min(1, w / S);
  return (
    <div ref={ref} className="relative w-full" style={{ height: S * s }} data-testid="ai-stage">
      <div className="absolute left-1/2 top-0" style={{ width: S, height: S, marginLeft: -S / 2, transform: `scale(${s})`, transformOrigin: "top center" }}>
        <svg viewBox={`0 0 ${S} ${S}`} className="absolute inset-0 h-full w-full" aria-hidden>
          {aiChips.map((c, i) => <Line key={c.id} i={i} p={p} consent={consent} />)}
        </svg>
        <Hub p={p} consent={consent} />
        {aiChips.map((c, i) => <ChipNode key={c.id} chip={c} i={i} p={p} consent={consent} />)}
      </div>
    </div>
  );
};

const StepLine = ({ step, p }) => {
  const opacity = useTransform(p, step.range, [0.22, 1]);
  const x = useTransform(p, step.range, [-8, 0]);
  return (
    <motion.li style={{ opacity, x }} className="flex items-center gap-3" data-testid={`ai-step-${step.id}`}>
      <span className="flex shrink-0 -space-x-2">
        {step.avatars.map((a) => <img key={a} src={people[a].avatar} alt="" className="h-9 w-9 rounded-full border-2 border-white object-cover" />)}
      </span>
      <span className="text-[19px] font-semibold leading-snug tracking-[-0.02em] text-[#08090D]">{step.text}</span>
    </motion.li>
  );
};

export const AIScene = ({ consent, setConsent }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const override = useMotionValue(-1);
  const playing = useRef(null);
  const p = useTransform([scrollYProgress, override], ([s, o]) => (o >= 0 ? o : s));
  const recIn = useTransform(p, [0.74, 0.86], [0, 1]);
  const recX = useTransform(recIn, [0, 1], [40, 0]);
  const recPE = useTransform(recIn, (v) => (v > 0.5 ? "auto" : "none"));

  useMotionValueEvent(scrollYProgress, "change", () => {
    if (!playing.current && override.get() >= 0) override.set(-1);
  });

  const replay = () => {
    playing.current?.stop();
    override.set(0);
    playing.current = animate(override, 1, { duration: 7, ease: "linear", onComplete: () => (playing.current = null) });
  };

  return (
    <div ref={ref} className="relative h-[360vh]" data-testid="ai-scene">
      <div className="sticky top-0 flex h-screen items-center pt-16">
        <div className="mx-auto grid w-full max-w-[1320px] grid-cols-[minmax(0,280px)_minmax(0,1fr)_minmax(0,260px)] items-center gap-8 px-10 xl:grid-cols-[minmax(0,320px)_minmax(0,1fr)_minmax(0,280px)] xl:gap-12">
          <div>
            <p className="eyebrow">Sample scenario</p>
            <ol className="mt-5 space-y-5">
              {aiSteps.map((s) => <StepLine key={s.id} step={s} p={p} />)}
            </ol>
            <AIControls consent={consent} setConsent={setConsent} onReplay={replay} />
          </div>
          <Stage p={p} consent={consent} />
          <motion.div style={{ opacity: recIn, x: recX, pointerEvents: recPE }}>
            <AIRecommendationCard consent={consent} />
          </motion.div>
        </div>
      </div>
    </div>
  );
};
